import { useEffect, useMemo, useState } from "react";
import { formatDate } from "@/lib/dateUtils";
import { ArrowRight, ChevronUp, Home, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";


const money = (value: unknown) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function GenerateSaleInvoice() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchRows = async (url: string) => {
    const response = await fetch(url, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.rows || [];
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const [creditSales, lubricantSales] = await Promise.all([
        fetchRows("/api/credit-sales"),
        fetchRows("/api/lubricant-sales"),
      ]);

      const pendingCredit = creditSales.filter(
        (sale: any) => !sale.bill_no || sale.bill_no === "0" || sale.bill_no === ""
      );
      const pendingLub = lubricantSales.filter(
        (sale: any) => !sale.billNo && !sale.bill_no || sale.billNo === "0" || sale.bill_no === "0" || sale.billNo === "" || sale.bill_no === ""
      );

      const allTransactions = [
        ...pendingCredit.map((sale: any) => ({
          id: `credit-${sale.id}`,
          rawId: sale.id,
          date: sale.sale_date,
          transaction_type: "Credit Sale",
          org_cust: sale.organization_name || sale.organizationName || "",
          vehicle_no: sale.vehicle_number || sale.vehicleNumber || "",
          product: sale.product_name || sale.productName || "",
          quantity: sale.quantity || 0,
          price: sale.price_per_unit || sale.pricePerUnit || 0,
          cost: sale.total_amount || sale.totalAmount || 0,
          discount: sale.discount || 0,
          misc_charges: sale.misc_charges || sale.miscCharges || 0,
          emp_name: sale.employee_name || sale.employeeName || "",
          endpoint: `/api/credit-sales/${sale.id}`,
          rawTxn: sale,
        })),
        ...pendingLub.map((sale: any) => ({
          id: `lub-${sale.id}`,
          rawId: sale.id,
          date: sale.sale_date,
          transaction_type: "Lubricants",
          org_cust: sale.customerName || sale.customer_name || "",
          vehicle_no: sale.vehicleNumber || sale.vehicle_number || "",
          product: sale.product || sale.productName || "",
          quantity: sale.quantity || 0,
          price: sale.price_per_unit || sale.sale_rate || sale.saleRate || 0,
          cost: sale.amount || sale.totalAmount || 0,
          discount: sale.discount || 0,
          misc_charges: sale.misc_charges || sale.miscCharges || 0,
          emp_name: sale.employeeName || sale.employee_name || "",
          endpoint: `/api/lubricant-sales/${sale.id}`,
          rawTxn: sale,
        })),
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setTransactions(allTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoices = async () => {
    const selectedRows = transactions.filter((t) => selectedIds[t.id]);
    if (selectedRows.length === 0) {
      toast({
        title: "No transactions selected",
        description: "Please select at least one transaction to generate a bill.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const [allCredit, allLub] = await Promise.all([
        fetchRows("/api/credit-sales"),
        fetchRows("/api/lubricant-sales"),
      ]);

      let maxBill = 0;
      allCredit.forEach((s: any) => {
        const b = s.bill_no || s.billNo;
        if (b && b !== "0") {
          const num = parseInt(b, 10);
          if (!isNaN(num) && num > maxBill) maxBill = num;
        }
      });
      allLub.forEach((s: any) => {
        const b = s.billNo || s.bill_no;
        if (b && b !== "0") {
          const num = parseInt(b, 10);
          if (!isNaN(num) && num > maxBill) maxBill = num;
        }
      });

      const groups: Record<string, any[]> = {};
      selectedRows.forEach((row) => {
        const key = row.org_cust || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(row);
      });

      let nextBillNum = maxBill;
      const promises: Promise<any>[] = [];

      for (const [org, rows] of Object.entries(groups)) {
        nextBillNum += 1;
        const newBillNo = String(nextBillNum).padStart(5, "0");

        rows.forEach((row) => {
          const raw = row.rawTxn;
          const isCredit = row.id.startsWith("credit-");

          if (isCredit) {
            const body = {
              sale_date: raw.sale_date,
              shift: raw.shift || "S-1",
              credit_customer_id: raw.credit_customer_id,
              vehicle_number: raw.vehicle_number,
              fuel_product_id: raw.fuel_product_id,
              quantity: raw.quantity,
              price_per_unit: raw.price_per_unit,
              discount: raw.discount,
              advance: raw.advance,
              misc_charges: raw.misc_charges,
              indent_no: raw.indent_no,
              bill_no: newBillNo,
              employee_id: raw.employee_id,
              effect: raw.effect,
              effect_to: raw.effect === "Yes" ? raw.effect_to : null,
              description: raw.description,
              meter_reading: raw.meter_reading,
              mileage: raw.mileage,
            };

            promises.push(
              fetch(`/api/credit-sales/${row.rawId}`, {
                method: "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              }).then((res) => {
                if (!res.ok) throw new Error(`Failed to update credit sale ${row.rawId}`);
              })
            );
          } else {
            const body = {
              ...raw,
              billNo: newBillNo,
              bill_no: newBillNo,
            };

            promises.push(
              fetch(`/api/lubricant-sales/${row.rawId}`, {
                method: "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              }).then((res) => {
                if (!res.ok) throw new Error(`Failed to update lubricant sale ${row.rawId}`);
              })
            );
          }
        });
      }

      await Promise.all(promises);

      toast({
        title: "Bill Generated Successfully",
        description: `Generated bill(s) up to No. ${String(nextBillNum).padStart(5, "0")}`,
      });

      setSelectedIds({});
      await fetchTransactions();
      navigate("/generated-invoices");
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Generation Failed",
        description: err.message || "Failed to generate bill/invoice.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return transactions;

    return transactions.filter((row) =>
      [
        row.transaction_type,
        row.org_cust,
        row.vehicle_no,
        row.product,
        row.emp_name,
        formatDate(row.date),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [searchQuery, transactions]);

  const allVisibleSelected =
    filteredTransactions.length > 0 && filteredTransactions.every((row) => selectedIds[row.id]);

  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      filteredTransactions.forEach((row) => {
        next[row.id] = checked;
      });
      return next;
    });
  };

  const deleteRows = async (rows: any[]) => {
    await Promise.all(
      rows.map(async (row) => {
        const response = await fetch(row.endpoint, {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || "Unable to delete transaction");
        }
      })
    );

    setTransactions((prev) => prev.filter((row) => !rows.some((deleted) => deleted.id === row.id)));
    setSelectedIds((prev) => {
      const next = { ...prev };
      rows.forEach((row) => delete next[row.id]);
      return next;
    });
  };

  const handleDeleteOne = async (row: any) => {
    if (!window.confirm("Delete this transaction?")) return;

    try {
      await deleteRows([row]);
      toast({ title: "Deleted", description: "Transaction deleted successfully." });
    } catch (error: any) {
      toast({
        title: "Delete failed",
        description: error.message || "Unable to delete transaction.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 bg-slate-50 min-h-full">
      <div className="h-11 -mx-6 -mt-6 px-6 flex items-center bg-gradient-to-r from-slate-100 to-white border-b border-slate-100">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Home className="h-4 w-4 text-slate-900" />
          <span className="font-bold text-slate-900">Dashboard</span>
          <span className="text-slate-400">›</span>
          <span>Generate Bill</span>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-lg">
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-8 border-blue-600 px-3 text-xs font-medium text-blue-700 hover:text-blue-800 disabled:opacity-50"
              onClick={handleGenerateInvoices}
              disabled={loading || Object.keys(selectedIds).length === 0}
            >
              Generate Bill / Invoice
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-8 rounded-none border-slate-900 px-3 text-xs font-bold text-slate-900 hover:bg-slate-50"
              onClick={() => navigate("/generated-invoices")}
            >
              <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
              Go To Records
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-900">Search:</span>
            <div className="relative">
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-8 w-[220px] rounded-lg border-slate-300 pr-9 text-xs"
              />
              <Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3 text-xs text-slate-900">
          <span>Show</span>
          <select className="h-8 w-[72px] border border-slate-300 bg-white px-3 text-xs outline-none">
            <option>All</option>
          </select>
          <span>entries</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] table-fixed border-collapse text-xs">
            <colgroup>
              <col className="w-[5%]" />
              <col className="w-[5%]" />
              <col className="w-[10%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[9%]" />
              <col className="w-[15%]" />
              <col className="w-[7%]" />
              <col className="w-[7%]" />
              <col className="w-[8%]" />
              <col className="w-[7%]" />
              <col className="w-[8%]" />
              <col className="w-[10%]" />
              <col className="w-[8%]" />
              <col className="w-[5%]" />
            </colgroup>
            <thead>
              <tr className="border-y border-slate-300 text-slate-950">
                <th className="px-3 py-3 text-left">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={(value) => toggleAllVisible(Boolean(value))}
                    className="h-3.5 w-3.5 rounded-none border-slate-400"
                  />
                </th>
                <th className="px-3 py-3 text-left">
                  <ChevronUp className="h-3.5 w-3.5 text-slate-900" />
                </th>
                <th className="px-3 py-3 text-left font-bold">S.No</th>
                <th className="px-3 py-3 text-left font-bold">Date</th>
                <th className="px-3 py-3 text-left font-bold">Transaction Type</th>
                <th className="px-3 py-3 text-left font-bold">Org/Cust</th>
                <th className="px-3 py-3 text-left font-bold">Vehicle No</th>
                <th className="px-3 py-3 text-left font-bold">Product</th>
                <th className="px-3 py-3 text-right font-bold">Quantity</th>
                <th className="px-3 py-3 text-right font-bold">Price</th>
                <th className="px-3 py-3 text-right font-bold">Cost</th>
                <th className="px-3 py-3 text-right font-bold">Discount</th>
                <th className="px-3 py-3 text-right font-bold">Misc.Charges</th>
                <th className="px-3 py-3 text-left font-bold">Emp Name</th>
                <th className="px-3 py-3 text-center font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={15} className="px-3 py-10 text-center text-slate-500">
                    Loading transactions...
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-3 py-10 text-center text-slate-500">
                    No transactions found.
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((row, index) => (
                  <tr key={row.id} className="border-b border-slate-200 odd:bg-slate-50 even:bg-white">
                    <td className="px-3 py-2.5">
                      <Checkbox
                        checked={Boolean(selectedIds[row.id])}
                        onCheckedChange={(value) =>
                          setSelectedIds((prev) => ({ ...prev, [row.id]: Boolean(value) }))
                        }
                        className="h-3.5 w-3.5 rounded-none border-slate-400"
                      />
                    </td>
                    <td className="px-3 py-2.5" />
                    <td className="px-3 py-2.5 text-slate-900">{index + 1}</td>
                    <td className="px-3 py-2.5 text-slate-900">{formatDate(row.date)}</td>
                    <td className="px-3 py-2.5 text-slate-900">{row.transaction_type}</td>
                    <td className="truncate px-3 py-2.5 text-slate-900">{row.org_cust}</td>
                    <td className="truncate px-3 py-2.5 text-slate-900">{row.vehicle_no}</td>
                    <td className="truncate px-3 py-2.5 text-slate-900">{row.product}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{money(row.quantity)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{money(row.price)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{money(row.cost)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{money(row.discount)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{money(row.misc_charges)}</td>
                    <td className="truncate px-3 py-2.5 text-slate-900">{row.emp_name}</td>
                    <td className="px-3 py-2.5 text-center">
                      <button
                        type="button"
                        className="inline-flex text-red-600 hover:text-red-700"
                        title="Delete transaction"
                        onClick={() => handleDeleteOne(row)}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-900 px-1 py-3 text-xs text-slate-900">
          Showing {filteredTransactions.length === 0 ? 0 : 1} to {filteredTransactions.length} of{" "}
          {filteredTransactions.length} entries
        </div>
      </div>
    </div>
  );
}
